/**
 * 書き出しの前に出す確認ダイアログ(#259)。
 *
 * **ビューアの中(Shadow root の内側)に描く。** ブラウザ標準の `confirm` は
 * ボタン名を変えられず、**埋め込み先の iframe の `sandbox` で塞がれることもある。**
 *
 * **`<dialog>` の `showModal` を使う。** 最上位のレイヤーに出るので盤面や
 * 書き出し中の覆いより上に来て、**後ろの操作を受け付けず、Esc も効く。**
 */

/**
 * 確認を**順に**尋ね、**1 つでも断られたらそこで止める**(#259)。
 *
 * 動画の書き出しでは「利用上の注意 → 枠不足の確認」の順に出す(同意書 Q3)。
 * **先で断られたら後は尋ねない** —— キャンセルしたのに次の確認が出ると、
 * 取り消せていないように見える。
 */
export async function askInOrder(
  steps: readonly (() => boolean | Promise<boolean>)[],
): Promise<boolean> {
  for (const step of steps) {
    if (!(await step())) return false;
  }
  return true;
}

export interface NoticeDialogLabels {
  /** 本文。**改行はそのまま出す**(`white-space: pre-line`)。 */
  readonly body: string;
  readonly cancel: string;
}

export class NoticeDialog {
  readonly element: HTMLDialogElement;
  private readonly body: string;
  private readonly confirmButton: HTMLButtonElement;
  /** 開いている間の応答先。`null` なら閉じている。 */
  private resolve: ((confirmed: boolean) => void) | null = null;

  constructor(labels: NoticeDialogLabels) {
    this.body = labels.body;

    this.element = document.createElement('dialog');
    this.element.className = 'signa-dialog';

    /*
     * **中身は内側の箱に入れる。** `<dialog>` 自体には余白を持たせないので、
     * `<dialog>` 自身へのクリック = **背景(::backdrop)のクリック**と判定できる。
     */
    const panel = document.createElement('div');
    panel.className = 'signa-dialog-panel';

    const text = document.createElement('p');
    text.className = 'signa-dialog-body';
    text.id = 'signa-dialog-body';
    // **`textContent` で入れる。** HTML として解釈させない。
    text.textContent = labels.body;
    this.element.setAttribute('aria-describedby', text.id);

    const actions = document.createElement('div');
    actions.className = 'signa-dialog-actions';

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'signa-button';
    cancelButton.textContent = labels.cancel;
    cancelButton.addEventListener('click', () => {
      this.finish(false);
    });

    this.confirmButton = document.createElement('button');
    this.confirmButton.type = 'button';
    this.confirmButton.className = 'signa-button signa-button-primary';
    this.confirmButton.addEventListener('click', () => {
      this.finish(true);
    });

    actions.append(cancelButton, this.confirmButton);
    panel.append(text, actions);
    this.element.append(panel);

    /*
     * **Esc・背景のクリック・キャンセルはすべて断った扱い**(同意書)——
     * 注意を読まずに閉じたものを了承とみなさない。
     */
    this.element.addEventListener('click', (event) => {
      if (event.target === this.element) this.finish(false);
    });
    this.element.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.finish(false);
    });
    /*
     * **結果は閉じる操作の側で返す。** `close` イベントに頼らない ——
     * Esc で閉じたときに `close` が届かない環境があり(#259 で確認)、
     * **応答が返らないと以後ダイアログが開かなくなる。**
     * ここは上以外の経路で閉じられたときの受け皿。
     *
     * **開き直した後に前回の `close` が遅れて届いても、今回を閉じない。**
     */
    this.element.addEventListener('close', () => {
      if (!this.element.open) this.finish(false);
    });
  }

  /** 閉じて結果を返す。**2 度目以降は何もしない。** */
  private finish(confirmed: boolean): void {
    const resolve = this.resolve;
    this.resolve = null;
    if (this.element.open) this.element.close();
    resolve?.(confirmed);
  }

  /**
   * 注意を出し、**確定ボタンが押されたら `true`** を返す。
   *
   * @param confirmLabel 確定ボタンの文字(動画は「書き出す」、静止画は「保存する」)
   */
  ask(confirmLabel: string): Promise<boolean> {
    // **二重に開かない。** 開いている間の呼び出しは断ったものとして扱う。
    if (this.resolve !== null) return Promise.resolve(false);

    // `<dialog>` を持たない古い環境では、ボタン名は変わらないが**注意は必ず出す。**
    if (typeof this.element.showModal !== 'function') {
      return Promise.resolve(globalThis.confirm(this.body));
    }

    this.confirmButton.textContent = confirmLabel;

    return new Promise((resolve) => {
      this.resolve = resolve;
      this.element.showModal();
      // **確定ボタンに入れる**(同意書)。Enter で進め、Esc で戻れる。
      this.confirmButton.focus();
    });
  }
}
